import { CSSProperties, ReactElement } from 'react'

import classNames from 'classnames'

import { CommonNIcon, HeaderBackIcon, CommonClose2Icon, CommonWrite4Icon, HeaderMyLabelIcon } from '@local/assets/icons'

import useScroll from '@web/hooks/useScroll'
import { DEFAULT_PROFILE_IMAGE } from '@web/libs/constants'
import Clickable from '@web/ui/components/Clickable'
import { HEIGHT_HEADER } from '@web/ui/components/Header/constant'
import Image from '@web/ui/components/Image'

import styles from './Header.module.scss'
import type { HomeHeaderProps } from './index'

export interface HeaderProps
  extends Pick<
    HomeHeaderProps,
    'nickname' | 'onMyplaceClick' | 'onNaverClick' | 'profileImageUrl' | 'showProfileOnScroll'
  > {
  noBorder?: boolean
  onCloseClick?: () => void
  closeClickCode?: string
  onPrevClick?: () => void
  onProfileClick?: () => void
  onClickProfileMy?: () => void // 홈으로 랜딩괴는 유저 프로필 버튼
  onReviewWriteClick?: () => void
  borderImageUrl?: string | null
  showBackButton?: boolean
  showCloseButton?: boolean
  showMyPlaceLogo?: boolean
  showReviewWriteButton?: boolean
  title?: string | null | ReactElement
  transparentBg?: boolean
  style?: CSSProperties
  className?: Record<string, boolean | undefined>
  dummyHeight?: number
  showMy?: boolean
  clickCode?: {
    close?: string
    back?: string
    my?: string
  }
}

export function Header(props: HeaderProps) {
  const {
    nickname,
    noBorder,
    onCloseClick,
    closeClickCode,
    clickCode,
    onMyplaceClick,
    onPrevClick,
    onProfileClick,
    onClickProfileMy,
    onNaverClick,
    onReviewWriteClick,
    profileImageUrl,
    borderImageUrl,
    showBackButton = true,
    showCloseButton = false,
    showMyPlaceLogo,
    showProfileOnScroll,
    showReviewWriteButton = false,
    title,
    transparentBg,
    className,
    dummyHeight, // = HEIGHT_HEADER
    showMy,
    style,
  } = props

  const thresholdShowProfile = useScroll((_: number, y: number) => y > HEIGHT_HEADER)
  const thresholdShowBorder = useScroll((_: number, y: number) => y > 0)

  return (
    <>
      <header
        className={classNames(styles.header, styles.type_simple, styles.type_fixed, {
          [styles.type_transparent_bg]: transparentBg && !thresholdShowBorder,
          [styles.type_show_border]: !noBorder && thresholdShowBorder,
          ...className,
        })}
        style={style}
      >
        <div className={styles.inner}>
          {showBackButton && !(showProfileOnScroll && thresholdShowProfile) && (
            <div className={classNames(styles.btn_area, styles.type_position_left)}>
              <Clickable className={styles.btn} onClick={onPrevClick} nClickCode={clickCode?.back}>
                <HeaderBackIcon className={styles.icon} aria-hidden="true" />
                <span className="place_blind">이전 페이지</span>
              </Clickable>
            </div>
          )}
          <h1 className={styles.title_area}>
            {showProfileOnScroll && thresholdShowProfile ? (
              <Clickable className={styles.profile_wrapper} onClick={onProfileClick}>
                <div className={styles.profile_thumb_area}>
                  <div className={styles.thumb_area}>
                    <Image
                      className={styles.img}
                      src={profileImageUrl ?? DEFAULT_PROFILE_IMAGE}
                      width="30"
                      height="30"
                      alt="프로필"
                    />
                    {borderImageUrl && (
                      <Image
                        className={styles.img_profile_border}
                        src={borderImageUrl}
                        width="40"
                        height="40"
                        alt="프로필 띠"
                      />
                    )}
                  </div>
                </div>
                <div className={styles.profile_info_area}>
                  <div className={styles.name_area}>
                    <span className={styles.name}>{nickname}</span>
                  </div>
                </div>
              </Clickable>
            ) : (
              <div className={styles.page_name_wrapper}>
                {showMyPlaceLogo ? (
                  <>
                    <Clickable className={styles.logo_naver} onClick={onNaverClick}>
                      <CommonNIcon className={styles.icon} aria-hidden="true" />
                      <span className="place_blind">네이버</span>
                    </Clickable>
                    <Clickable className={styles.logo_myplace} onClick={onMyplaceClick}>
                      MY플레이스
                    </Clickable>
                  </>
                ) : (
                  <div className={styles.page_name_area}>
                    <span className={styles.name}>{title}</span>
                  </div>
                )}
              </div>
            )}
          </h1>
          <div className={classNames(styles.btn_area, styles.type_position_right)}>
            {showCloseButton && (
              <Clickable className={styles.btn} onClick={onCloseClick} nClickCode={clickCode?.close ?? closeClickCode}>
                <CommonClose2Icon className={styles.icon} aria-hidden="true" />
                <span className="place_blind">닫기</span>
              </Clickable>
            )}
            {showReviewWriteButton && (
              <Clickable className={styles.btn_write} onClick={onReviewWriteClick}>
                <CommonWrite4Icon className={styles.icon} aria-hidden="true" />
                리뷰 참여
              </Clickable>
            )}
          </div>
          {showMy && (
            <Clickable className={styles.btn_my} onClick={onClickProfileMy} nClickCode={clickCode?.my}>
              <div className={styles.profile_area}>
                <Image
                  className={styles.img}
                  src={profileImageUrl ?? DEFAULT_PROFILE_IMAGE}
                  width={32}
                  height={32}
                  alt="프로필"
                />
                {borderImageUrl && (
                  <div className={styles.profile_border}>
                    <Image src={borderImageUrl} width="100%" height="100%" alt="프로필 띠" />
                  </div>
                )}
              </div>
              <HeaderMyLabelIcon className={styles.icon_label} aria-hidden="true" />
              <span className="place_blind">마이플레이스</span>
            </Clickable>
          )}
        </div>
      </header>
      {/* Note: fixed 시 높이 보정을 위한 더미 구조 */}
      {dummyHeight && <div style={{ height: dummyHeight }} />}
    </>
  )
}
